/** «Отсутствие» dialog (task 23 §5.3), backed by the event-type catalog. */
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/ui/date-field";
import { Select } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showApiErrorToast } from "@/lib/api-error-toast";
import {
  useCreateScheduleEvent,
  usePersonEventTypes,
  useUpdateScheduleEvent,
} from "@/hooks/usePeople";
import type { PersonScheduleEvent } from "@/api/people";
import { ABSENCE_EVENT_OPTIONS } from "@/lib/person-schedule-events";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import {
  apiErrorFormIssue,
  FormErrorSummary,
  FormFieldError,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";

export interface AbsenceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: number;
  /**
   * Прайм-дата для полей «С»/«По» при открытии (ISO `YYYY-MM-DD`). Мастерский
   * флоу учёта времени (задача 82 §1.2) открывает диалог на выбранном дне —
   * date_from/date_to преднабиваются им, мастер только сдвигает «По» под
   * диапазон. На карточке `/people/$id` проп не передаётся → поля пустые.
   */
  defaultDate?: string;
  initialEvent?: PersonScheduleEvent;
}

const isoDate = /^\d{4}-\d{2}-\d{2}$/;

const formSchema = z
  .object({
    event_type_id: z.number().int().positive(),
    date_from: z.string().regex(isoDate),
    date_to: z.string().regex(isoDate),
    hours_off: z.number().multipleOf(0.5, "hoursRange").min(0, "hoursRange").max(24, "hoursRange"),
    reason: z.string().max(500),
  })
  .refine((v) => v.date_to >= v.date_from, { path: ["date_to"], message: "dateOrder" });

type AbsenceValues = z.infer<typeof formSchema>;

const DEFAULTS: AbsenceValues = {
  event_type_id: 0,
  date_from: "",
  date_to: "",
  hours_off: 0,
  reason: "",
};

export function AbsenceFormDialog({
  open,
  onOpenChange,
  userId,
  defaultDate,
  initialEvent,
}: AbsenceFormDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const createMut = useCreateScheduleEvent();
  const updateMut = useUpdateScheduleEvent();
  const eventTypesQuery = usePersonEventTypes();
  const [serverError, setServerError] = useState<FormValidationIssue | null>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<AbsenceValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULTS,
  });
  useReleaseDirtyState(open && isDirty);

  useEffect(() => {
    if (open) {
      setServerError(null);
      reset(
        initialEvent
          ? {
              event_type_id: initialEvent.event_type_id,
              date_from: initialEvent.date_from,
              date_to: initialEvent.date_to,
              hours_off: (initialEvent.duration_half_hours ?? 0) / 2,
              reason: initialEvent.reason,
            }
          : defaultDate
            ? { ...DEFAULTS, date_from: defaultDate, date_to: defaultDate }
            : DEFAULTS,
      );
    }
  }, [open, reset, defaultDate, initialEvent]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const eventTypeId = watch("event_type_id");
  const absenceTypes = (eventTypesQuery.data ?? []).filter(
    (item) =>
      item.event_group === "absence" && (item.is_active || item.id === initialEvent?.event_type_id),
  );
  const selectedType = absenceTypes.find((item) => item.id === eventTypeId);
  const isPartTime = selectedType?.seed_key === "part_time";

  useEffect(() => {
    if (!open || initialEvent || eventTypeId > 0 || absenceTypes.length === 0) return;
    setValue("event_type_id", absenceTypes[0].id, { shouldValidate: true });
  }, [absenceTypes, eventTypeId, initialEvent, open, setValue]);

  async function onSubmit(values: AbsenceValues): Promise<void> {
    setServerError(null);
    const eventType = absenceTypes.find((item) => item.id === values.event_type_id);
    if (!eventType) {
      const message = t("people.toast.addFailed");
      setServerError({ id: "absence-form-server-error", message, kind: "server" });
      toast.error(message);
      return;
    }
    try {
      const body = {
        date_from: values.date_from,
        date_to: values.date_to,
        event_type_id: eventType.id,
        is_all_day: !isPartTime,
        duration_half_hours: isPartTime ? values.hours_off * 2 : null,
        payable:
          ABSENCE_EVENT_OPTIONS.find((item) => item.seedKey === eventType.seed_key)?.payable ??
          initialEvent?.payable ??
          false,
        reason: values.reason,
      };
      if (initialEvent) {
        await updateMut.mutateAsync({ userId, eventId: initialEvent.id, body });
      } else {
        await createMut.mutateAsync({ userId, body });
      }
      toast.success(t("people.toast.added"));
      onOpenChange(false);
    } catch (err) {
      setServerError(
        apiErrorFormIssue(err, {
          id: "absence-form-server-error",
          fallbackMessage: t("common.apiError.fallback"),
        }),
      );
      showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
    }
  }

  function describeError(name: keyof AbsenceValues): string | undefined {
    const msg = errors[name]?.message;
    if (!msg) return undefined;
    if (msg === "dateOrder") return t("people.errors.dateOrder");
    if (msg === "hoursRange") return t("people.errors.hoursRange");
    return t("people.errors.required");
  }

  const fieldDefinitions = [
    ["event_type_id", "absence-field-kind"],
    ["date_from", "absence-field-date-from"],
    ["date_to", "absence-field-date-to"],
    ["hours_off", "absence-field-hours"],
  ] as const;
  const issues: FormValidationIssue[] = [];
  for (const [name, controlId] of fieldDefinitions) {
    const message = describeError(name);
    if (message) issues.push({ id: `${controlId}-error`, message, kind: "field", controlId });
  }
  if (serverError) issues.push(serverError);

  function issueFor(name: keyof AbsenceValues): FormValidationIssue | undefined {
    const controlId = fieldDefinitions.find(([fieldName]) => fieldName === name)?.[1];
    return issues.find((issue) => issue.id === `${controlId}-error`);
  }

  function handleInvalid(invalidErrors: typeof errors): void {
    const controlId = fieldDefinitions.find(([name]) => invalidErrors[name])?.[1];
    if (controlId) requestAnimationFrame(() => focusValidationControl(controlId));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="absence-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {t(initialEvent ? "people.absenceForm.editTitle" : "people.absenceForm.title")}
          </DialogTitle>
          <DialogDescription>{t("people.absenceForm.desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit, handleInvalid)} className="space-y-3" noValidate>
          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("people.absenceForm.kind")}
            </span>
            <Select
              id="absence-field-kind"
              data-testid="absence-field-kind"
              value={eventTypeId > 0 ? String(eventTypeId) : ""}
              onChange={(v) => {
                setValue("event_type_id", Number(v), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                if (absenceTypes.find((item) => item.id === Number(v))?.seed_key !== "part_time") {
                  setValue("hours_off", 0, { shouldValidate: false });
                  clearErrors("hours_off");
                }
              }}
              placeholder={t("people.absenceForm.kind")}
              className="h-8"
              options={absenceTypes.map((eventType) => ({
                value: String(eventType.id),
                label: eventType.name,
              }))}
              aria-invalid={Boolean(issueFor("event_type_id"))}
              aria-describedby={issueFor("event_type_id")?.id}
            />
            <FormFieldError issue={issueFor("event_type_id")} />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="text-muted-foreground mb-1 block text-xs">
                {t("people.absenceForm.dateFrom")}
              </span>
              <Controller
                control={control}
                name="date_from"
                render={({ field }) => (
                  <DateField
                    id="absence-field-date-from"
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? "")}
                    label={t("people.absenceForm.dateFrom")}
                    name={field.name}
                    required
                    data-testid="absence-field-date-from"
                    aria-invalid={Boolean(issueFor("date_from"))}
                    aria-describedby={issueFor("date_from")?.id}
                  />
                )}
              />
              <FormFieldError issue={issueFor("date_from")} />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground mb-1 block text-xs">
                {t("people.absenceForm.dateTo")}
              </span>
              <Controller
                control={control}
                name="date_to"
                render={({ field }) => (
                  <DateField
                    id="absence-field-date-to"
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? "")}
                    label={t("people.absenceForm.dateTo")}
                    name={field.name}
                    required
                    data-testid="absence-field-date-to"
                    aria-invalid={Boolean(issueFor("date_to"))}
                    aria-describedby={issueFor("date_to")?.id}
                  />
                )}
              />
              <FormFieldError issue={issueFor("date_to")} />
            </label>
          </div>

          {isPartTime && (
            <label className="block text-sm">
              <span className="text-muted-foreground mb-1 block text-xs">
                {t("people.absenceForm.hoursOff")}
              </span>
              <Input
                id="absence-field-hours"
                type="number"
                step={0.5}
                data-testid="absence-field-hours"
                {...register("hours_off", { valueAsNumber: true })}
                aria-invalid={Boolean(issueFor("hours_off"))}
                aria-describedby={issueFor("hours_off")?.id}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t("people.absenceForm.hoursOffHint")}
              </p>
              <FormFieldError issue={issueFor("hours_off")} />
            </label>
          )}

          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("people.absenceForm.reason")}
            </span>
            <Input data-testid="absence-field-reason" {...register("reason")} />
          </label>

          <FormErrorSummary
            issues={issues}
            countLabel={(count) => t("validation.fieldErrorCount", { count })}
            data-testid="absence-form-error-summary"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="absence-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting} data-testid="absence-form-submit">
              {t(initialEvent ? "common.save" : "people.absenceForm.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
